import { collection, getDocs, query, where } from 'firebase/firestore';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import { db } from '../firebase';
import '../styles/Blog.css';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  // Function to format blog content for better readability
  const formatBlogContent = (content) => {
    if (!content) return '';
    
    // If content is already HTML, return as is
    if (content.includes('<p>') || content.includes('<h') || content.includes('<div>')) {
      return content;
    }
    
    // If content is plain text, preserve formatting exactly as typed
    let formattedContent = content;
    
    // Convert line breaks to <br> tags to preserve line breaks
    formattedContent = formattedContent.replace(/\n/g, '<br>');
    
    return formattedContent;
  };

  const fetchPost = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch from Firestore only
      const articlesRef = collection(db, 'articles');
      const q = query(articlesRef, where('slug', '==', slug));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        setPost({ id: String(doc.id), ...doc.data() }); // Ensure ID is string
      } else {
        setPost(null); // No article found
      }
    } catch (error) {
      console.error('Error fetching post:', error);
      setPost(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  if (loading) {
    return (
      <div className="blog-post">
        <div className="container">
          <div className="loading">
            <h1>Loading...</h1>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="blog-post">
        <div className="container">
          <div className="post-not-found">
            <h1>Article Not Found</h1>
            <p>The article you're looking for doesn't exist or has been removed.</p>
            <Link to="/blog" className="back-to-blog">
              ← Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-post">
      <SEO 
        title={`${post.title} | LogiCar Blog`}
        description={post.excerpt}
        canonical={`https://www.logicarapp.com/blog/${post.slug}`}
      />
      
      <div className="container">
        <div className="post-header">
          <div className="breadcrumb">
            <Link to="/">🏠 Home</Link> / <Link to="/blog">📝 Blog</Link> / <span>{post.title}</span>
          </div>
          
          <div className="post-meta">
            <span className="date">📅 {new Date(post.date).toLocaleDateString()}</span>
            <span className="read-time">⏱️ {post.readTime}</span>
            <span className="author">👤 {post.author}</span>
            <span className="category">🚗 {post.category || 'Automotive'}</span>
          </div>
          
          <h1>{post.title}</h1>
          {post.image && (
            <div className="post-hero-image">
              <img src={post.image} alt={post.title} />
            </div>
          )}
        </div>
        
        <div className="post-content">
          <div className="content-wrapper">
            <div dangerouslySetInnerHTML={{ __html: formatBlogContent(post.content) }} />
          </div>
        </div>
        
        <div className="post-footer">
          <div className="post-tags">
            <span>🏷️ Tags:</span>
            <span className="tag">Car Maintenance</span>
            <span className="tag">Automotive Tips</span>
            <span className="tag">Vehicle Care</span>
            <span className="tag">DIY</span>
          </div>
          
          <div className="post-navigation">
            <Link to="/blog" className="back-to-blog">
              ← Back to Blog
            </Link>
          </div>
        </div>
        
        <div className="related-posts">
          <h3>📚 Related Articles</h3>
          <div className="related-posts-grid">
            <div className="related-post-card">
              <h4>Essential Car Maintenance Checklist</h4>
              <p>Keep your vehicle running smoothly with our comprehensive maintenance guide.</p>
              <div className="post-date">January 15, 2024</div>
            </div>
            <div className="related-post-card">
              <h4>Winter Driving Safety Tips</h4>
              <p>Stay safe on winter roads with these essential driving and vehicle preparation tips.</p>
              <div className="post-date">December 20, 2023</div>
            </div>
            <div className="related-post-card">
              <h4>Understanding Your Car's Dashboard</h4>
              <p>Learn what all those lights and gauges mean and when to take action.</p>
              <div className="post-date">November 10, 2023</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;